import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { IChapter, IDraftChapter } from "models/wall";
import {
  deleteChapterApi,
  getChaptersByStoryId,
  moveChapterDownApi,
  moveChapterUpApi,
  toggleHideChapterApi
} from "api/chapters";
import { getDraftChaptersByStoryId, publishDraftChapter } from "api/draftChapters";

type WallDataContextType = {
  isCreator: boolean;
  chapters: IChapter[];
  draftChapters: IDraftChapter[];
  chaptersLoading: boolean;
  getChapters: () => void;
  getDraftChapters: () => void;
  removeChapter: (uuid: string) => void;
  removeDraftChapter: (uuid: string) => void;
  moveUp: (uuid: string) => void;
  moveDown: (uuid: string) => void;
  hideChapter: (uuid: string) => void;
  addChapter: (chapter: IChapter) => void;
  addDraftChapter: (chapter: IDraftChapter) => void;
  onDraftChapterPublish: (uuid: string) => void;
  updateDraftUuid: (chapterUuid: string, draftUuid: string | null) => void;
};

const WallDataContext = createContext<WallDataContextType | undefined>(
  undefined
);

export const WallDataProvider = ({children, storyId, isCreator}: {
  children: React.ReactNode;
  storyId: string;
  isCreator: boolean;
}) => {
  const [chapters, setChapters] = useState<IChapter[]>([]);
  const [draftChapters, setDraftChapters] = useState<IDraftChapter[]>([]);
  const [chaptersLoading, setChaptersLoading] = useState(true);
  const [draftChaptersLoading, setDraftChaptersLoading] = useState(true);

  const getChapters = useCallback(async () => {
    setChaptersLoading(true);
    try {
      const response = await getChaptersByStoryId(storyId);
      setChapters([...response.results].sort((a, b) => a.order - b.order));
    } catch (e) {
      console.log(e);
    } finally {
      setChaptersLoading(false);
    }
  }, [storyId]);

  const getDraftChapters = useCallback(async () => {
    setDraftChaptersLoading(true);
    try {
      const response = await getDraftChaptersByStoryId(storyId);
      setDraftChapters(response.results.filter(i => !i.original_chapter));
    } catch (e) {
      console.log(e);
    } finally {
      setDraftChaptersLoading(false);
    }
  }, [storyId]);

  const addChapter = (chapter: IChapter) => {
    setChapters((prev) => {
      const newArr = [...prev.filter(c => c.uuid !== chapter.uuid), chapter];

      return newArr.sort((a, b) => a.order - b.order);
    });
  };

  const addDraftChapter = (chapter: IDraftChapter) => {
    setDraftChapters(prev => [...prev.filter(c => c.uuid !== chapter.uuid), chapter]);
  };

  const removeChapter = async (uuid: string) => {
    try {
      await deleteChapterApi(uuid);
      setChapters(p => p.filter(chapter => chapter.uuid !== uuid));
    } catch (e) {
      console.log(e);
    }
  };

  const removeDraftChapter = async (uuid: string) => {
    try {
      await deleteChapterApi(uuid, true);
      setDraftChapters(p => p.filter(chapter => chapter.uuid !== uuid));
    } catch (e) {
      console.log(e);
    }
  };

  const removeDraftIdFromChapter = (uuid: string) => {
    const chapter = chapters.find(c => c.uuid === uuid);
    if (!chapter) return;
    const newChapter = {...chapter, draft_uuid: null};

    setChapters((prev) => {
      const newArr = [...prev.filter(chapter => chapter.uuid !== uuid), newChapter];

      return newArr.sort((a, b) => a.order - b.order);
    });
  };

  const onDraftChapterPublish = async (uuid: string) => {
    try {
      const res = await publishDraftChapter(uuid);
      addChapter(res);
      setDraftChapters(p => p.filter(chapter => chapter.uuid !== uuid));
    } catch (e) {
      console.log(e);
    }
  };

  const moveUp = async (uuid: string) => {
    const index = chapters.findIndex((chapter) => chapter.uuid === uuid);
    if (index <= 0) return;
    try {
      await moveChapterUpApi(uuid);
      setChapters((prevChapters) => {
        const copy = [...prevChapters];
        const index = copy.findIndex((chapter) => chapter.uuid === uuid);
        if (index <= 0) return prevChapters;

        const temp = copy[index].order;
        copy[index].order = copy[index - 1].order;
        copy[index - 1].order = temp;

        return copy.sort((a, b) => a.order - b.order);
      });
    } catch (e) {
      console.log(e);
    }
  }

  const moveDown = async (uuid: string) => {
    const index = chapters.findIndex((chapter) => chapter.uuid === uuid);
    if (index === chapters.length - 1 || index < 0) return;
    try {
      await moveChapterDownApi(uuid);
      setChapters((prevChapters) => {
        const copy = [...prevChapters];
        const index = copy.findIndex((chapter) => chapter.uuid === uuid);
        if (index === chapters.length - 1 || index < 0) return prevChapters;

        const temp = copy[index].order;
        copy[index].order = copy[index + 1].order;
        copy[index + 1].order = temp;

        return copy.sort((a, b) => a.order - b.order);
      });
    } catch (e) {
      console.log(e);
    }
  }

  const hideChapter = async (uuid: string) => {
    const chapter = chapters.find((chapter) => chapter.uuid === uuid);
    if (!chapter) return;
    try {
      await toggleHideChapterApi(uuid, chapter.is_hidden);
      setChapters((prevChapters) => {
        const copy = [
          ...prevChapters.filter(c => c.uuid !== uuid),
          {...chapter, is_hidden: !chapter.is_hidden}
        ];

        return copy.sort((a, b) => a.order - b.order);
      });
    } catch (e) {
      console.log(e);
    }
  }

  const updateDraftUuid = (chapterUuid: string, draftUuid: string | null) => {
    setChapters(prev =>
      prev.map(ch =>
        ch.uuid === chapterUuid ? { ...ch, draft_uuid: draftUuid } : ch
      )
    );
  };

  useEffect(() => {
    getChapters();
  }, [getChapters]);

  useEffect(() => {
    getDraftChapters();
  }, [getDraftChapters]);

  return (
    <WallDataContext.Provider value={{
      isCreator,
      chapters,
      getChapters,
      draftChapters,
      chaptersLoading: chaptersLoading || draftChaptersLoading,
      getDraftChapters,
      removeChapter,
      removeDraftChapter,
      moveUp,
      moveDown,
      hideChapter,
      addChapter,
      addDraftChapter,
      onDraftChapterPublish,
      updateDraftUuid,
    }}>
      {children}
    </WallDataContext.Provider>
  );
};

export const useWallDataContext = () => {
  const context = useContext(WallDataContext);
  if (!context) {
    throw new Error("useWallContext must be used within a WallProvider");
  }
  return context;
};
